/* Flayvr Taste Profile results presentation + viewport motion v2.0
   Presentation only. Does not change profile scoring, restaurant ranking,
   map logic, share/compare or PDF data. */
(()=>{
  'use strict';

  const reduced=()=>window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const observed=new WeakSet();
  const counted=new WeakSet();
  let motionObserver=null;
  let numberObserver=null;
  let mutationObserver=null;
  let scanQueued=false;

  const motionSelectors=[
    '.tpv-radar',
    '.tpv-score-row',
    '.tpv-confidence-row',
    '.tpv-evo-compare-row',
    '.tpv-ring-item',
    '.tpv2-signal'
  ];

  const numberSelectors=[
    '[data-tpv-score]',
    '[data-tpv-ring-score]',
    '.tpv-cuisine-match',
    '.tpv-pick [data-match]',
    '[data-tpv-match-score]',
    '.tpv2-signal>div:first-child>b',
    '.tpv2-rest-top>b',
    '.tpv2-world-score>b',
    '.tpv2-cities b',
    '.tpv2-bottom-grid .tpv2-pink'
  ];

  function activateRoot(){
    document.querySelectorAll('[data-tpv-root]').forEach(root=>{
      const results=root.querySelector('[data-tpv-results]');
      root.classList.toggle('tpv-results-unified-active',!!(results&&!results.hidden));
    });
  }

  function parseNumber(el){
    const raw=(el.textContent||'').trim();
    const m=raw.match(/-?\d+(?:\.\d+)?/);
    if(!m)return null;
    const value=Number(m[0]);
    if(!Number.isFinite(value))return null;
    const decimals=(m[0].split('.')[1]||'').length;
    const suffix=/%/.test(raw)?'%':'';
    return {value,decimals,suffix,raw};
  }

  function animateNumber(el){
    if(counted.has(el))return;
    const parsed=parseNumber(el);
    if(!parsed)return;
    counted.add(el);
    el.dataset.tprCount=String(parsed.value);

    if(reduced()){
      el.classList.add('tpr-count-done');
      return;
    }

    const {value,decimals,suffix}=parsed;
    const duration=900;
    const start=performance.now();
    el.classList.remove('tpr-count-waiting');
    el.classList.add('tpr-count-running');
    el.textContent=(0).toFixed(decimals)+suffix;

    const tick=now=>{
      const p=Math.min(1,(now-start)/duration);
      const eased=1-Math.pow(1-p,3);
      el.textContent=(value*eased).toFixed(decimals)+suffix;
      if(p<1){
        requestAnimationFrame(tick);
      }else{
        el.textContent=value.toFixed(decimals)+suffix;
        el.classList.remove('tpr-count-running');
        el.classList.add('tpr-count-done');
      }
    };
    requestAnimationFrame(tick);
  }

  function ensureMotionObserver(){
    if(motionObserver||!('IntersectionObserver' in window)||reduced())return motionObserver;
    motionObserver=new IntersectionObserver(entries=>{
      entries.forEach(entry=>{
        if(!entry.isIntersecting)return;
        entry.target.classList.add('tpr-inview');
        motionObserver.unobserve(entry.target);
      });
    },{
      root:null,
      /* A narrow lower-middle band means the item itself must be reached. */
      rootMargin:'-48% 0px -28% 0px',
      threshold:0
    });
    return motionObserver;
  }

  function ensureNumberObserver(){
    if(numberObserver||!('IntersectionObserver' in window)||reduced())return numberObserver;
    numberObserver=new IntersectionObserver(entries=>{
      entries.forEach(entry=>{
        if(!entry.isIntersecting)return;
        animateNumber(entry.target);
        numberObserver.unobserve(entry.target);
      });
    },{root:null,rootMargin:'-24% 0px -22% 0px',threshold:.01});
    return numberObserver;
  }

  function registerMotion(scope=document){
    motionSelectors.forEach(sel=>{
      const items=[];
      if(scope.matches?.(sel))items.push(scope);
      scope.querySelectorAll?.(sel).forEach(el=>items.push(el));
      items.forEach(el=>{
        if(observed.has(el))return;
        observed.add(el);
        if(reduced()||!('IntersectionObserver' in window))el.classList.add('tpr-inview');
        else ensureMotionObserver()?.observe(el);
      });
    });
  }

  function registerNumbers(scope=document){
    numberSelectors.forEach(sel=>{
      const items=[];
      if(scope.matches?.(sel))items.push(scope);
      scope.querySelectorAll?.(sel).forEach(el=>items.push(el));
      items.forEach(el=>{
        if(counted.has(el)||el.dataset.tprNumberObserved==='1')return;
        if(!parseNumber(el))return;
        el.dataset.tprNumberObserved='1';
        if(reduced()||!('IntersectionObserver' in window))animateNumber(el);
        else{
          el.classList.add('tpr-count-waiting');
          ensureNumberObserver()?.observe(el);
        }
      });
    });
  }

  function scan(scope=document){
    activateRoot();
    registerMotion(scope);
    registerNumbers(scope);
  }

  function queueScan(){
    if(scanQueued)return;
    scanQueued=true;
    requestAnimationFrame(()=>{
      scanQueued=false;
      scan(document);
    });
  }

  function boot(){
    /* Safe default remains fully visible until both observers are ready. */
    ensureMotionObserver();
    ensureNumberObserver();
    scan(document);
    /* Only opt into hidden pre-animation states after registration succeeds. */
    document.documentElement.classList.add('tpr-motion-ready');

    mutationObserver=new MutationObserver(records=>{
      let needs=false;
      for(const r of records){
        if(r.type==='attributes'&&r.attributeName==='hidden'){needs=true;break}
        if(r.addedNodes&&r.addedNodes.length){needs=true;break}
        /* Dynamic score text is populated by the frozen app after results mount. */
        if(r.type==='characterData'){needs=true;break}
      }
      if(needs)queueScan();
    });
    mutationObserver.observe(document.documentElement,{
      subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['hidden']
    });
  }

  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});
  else boot();
})();
